import {inject, Injectable} from '@angular/core';
import {share, Subscription} from 'rxjs';
import {Observable} from 'rxjs/internal/Observable';
import {fetchEventSource} from '@microsoft/fetch-event-source';
import {BackendService} from './backend.service';
import {StorageService} from './storage.service';

/*
 * Fataler Fehler, bei dem NICHT automatisch reconnected werden soll (z.B. ungültiges Token
 * oder fehlende Hub-URL). Wird exportiert, damit Consumer im error-Handler unterscheiden
 * können: bei einem fatalen Fehler bringt ein Retry nichts, weil sich ohne Zutun nichts ändert.
 */
export class FatalSseError extends Error {}

@Injectable({
    providedIn: 'root',
})
export class ServerSentEventsService {
    private backendService = inject(BackendService);
    private storageService = inject(StorageService);

    /*
     * Abonniert ein oder mehrere Mercure-Topics per SSE (Server-Sent Events) über EINE Verbindung;
     * topic(s) z.B. "calls" oder ["user/42", "notifications/broadcast"] - der Abonnent kriegt nur
     * Nachrichten, die zu einem der abonnierten Topics gehören. Mehrere Abonnenten teilen sich eine
     * Verbindung (siehe share()). Rückgabe ist ein Observable mit den empfangenen Events.
     */
    public listen<T>(topic: string | string[]) {
        const topics = Array.isArray(topic) ? topic : [topic];
        return new Observable<T>((subscriber) => {
            const controller = new AbortController();
            let tokenSubscription: Subscription | undefined;

            (async () => {
                // Hub-URL kommt aus der Backend-Config (sse.mercureHubUrl)
                const sseConfig = await this.storageService.getItem<{mercureHubUrl?: string | null}>('config|sse');
                if (controller.signal.aborted) {
                    return;
                }

                if (!sseConfig?.mercureHubUrl) {
                    /*
                     * console.error(
                     *     `[SSE] Keine Mercure-Hub-URL konfiguriert (sse.mercureHubUrl) — Topic "${topic}" kann nicht abonniert werden`,
                     * );
                     */
                    subscriber.error(new FatalSseError('Mercure-Hub-URL nicht konfiguriert (sse.mercureHubUrl)'));
                    return;
                }

                const url = new URL(sseConfig.mercureHubUrl);

                /*
                 * Auf welche "topic"s gehört bzw. abonniert werden soll (Mercure erlaubt mehrere topic-Params
                 * in einer Verbindung)
                 */
                topics.forEach((t) => url.searchParams.append('topic', t));

                // console.log(`[SSE] Verbinde mit Hub "${sseConfig.mercureHubUrl}", Topic "${topic}"`);

                /*
                 * Subscriber token abrufen
                 */
                tokenSubscription = this.backendService.getRequest('api/mercure/subscriberToken').subscribe({
                    next: ({data: {token}}) => {
                        // console.log(`[SSE] Subscriber-Token erhalten, öffne Verbindung für Topic "${topic}"...`);

                        fetchEventSource(url.toString(), {
                            signal: controller.signal,
                            /*
                             * Verbindung offen halten, auch wenn der Tab im Hintergrund ist (sonst verpassen wir Anrufe)
                             */
                            openWhenHidden: true,
                            //  Subscriber token mitschicken
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                            onopen: async (response) => {
                                if (!response.ok) {
                                    /*
                                     * console.error(
                                     *     `[SSE] Verbindung fehlgeschlagen für Topic "${topic}": HTTP ${response.status}`,
                                     * );
                                     */
                                    throw new FatalSseError(`SSE fehlgeschlagen: HTTP ${response.status}`);
                                }
                                // console.log(`[SSE] Verbindung geöffnet für Topic "${topic}" (HTTP ${response.status})`);
                            },

                            // Neue Nachricht vom Hub an alle Abonnenten weiterreichen
                            onmessage: (message) => {
                                // console.log(`[SSE] Nachricht erhalten auf Topic "${topic}":`, message.data);
                                subscriber.next(JSON.parse(message.data));
                            },
                            // Wird benutzt, um bei einem Idle-Timeout (nginx) zu reconnecten
                            onclose: () => {
                                /*
                                 * console.warn(
                                 *     `[SSE] Verbindung serverseitig geschlossen für Topic "${topic}", Reconnect wird versucht`,
                                 * );
                                 */
                                throw new Error('SSE-Verbindung serverseitig geschlossen');
                            },
                            onerror: (error) => {
                                // Fatale Fehler: hier wird nicht reconnected
                                if (error instanceof FatalSseError) {
                                    // console.error(`[SSE] Fataler Fehler für Topic "${topic}", kein Reconnect:`, error);
                                    subscriber.error(error);
                                    throw error;
                                }
                                /*
                                 * sonstige Fehler (z.B. kurzer Netzabbruch) -> automatischer reconnect
                                 * console.warn(
                                 *     `[SSE] Verbindungsfehler für Topic "${topic}" (automatischer Reconnect folgt):`,
                                 *     error,
                                 * );
                                 */
                            },
                        });
                    },
                    // Fehler der inneren Observable an die äußere weiterreichen, damit Fehler korrekt behandelt werden
                    error: (err) => {
                        /*
                         * console.error(
                         *     `[SSE] Subscriber-Token konnte nicht abgerufen werden für Topic "${topic}":`,
                         *     err,
                         * );
                         */
                        subscriber.error(err);
                    },
                });
            })();

            // SSE-Verbindung beenden, wenn es keine Abonnenten mehr gibt
            return () => {
                // console.log(`[SSE] Verbindung wird beendet für Topic "${topic}"`);
                tokenSubscription?.unsubscribe();
                controller.abort();
            };
        }).pipe(share());
    }
}
